// composables/useNotebook.ts
import { ref, reactive, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AxiosVideoService from "../axiosVideoService"
import axios from 'axios'
import { useUserStore } from "../../store/userStore";
const axiosWithToken = axios.create({
  withCredentials: true,  // axios 專用設定，不是 header
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.token ?? ''}`,
  },
});

const axiosVideoService = new AxiosVideoService()

export function useNotebook(
  videoId: string,
  isLogin: boolean,
  videoLang: Ref<string | undefined>,
  learning_language: Ref<string>,
  site_language: Ref<string>,
) {
  const { t } = useI18n()

  // 處理 loading 動畫顯示與提示訊息
  const loadingInfo = reactive({
    status: false,
    title: t('Video processing'),
  })

  // 處理錯誤提示用的 modal
  const modalInfo = reactive({
    type: "alert",
    status: false,
    title: "Fail!",
    content: t('subtitleGenerationFailed'),
    msg: "chatnow"
  })

  // 當前查詢的單字資料
  type VocabularyData = {
    id: string
    text: string
    user_saved: boolean
    pronunciation?: string
    meaning?: string
    language?: string
  }

  const vocabularyData = ref<VocabularyData>({
    id: '',
    text: '',
    user_saved: false,
  })


  /**
   * 儲存單句筆記
   */
  function saveSt(sentence: any, videoId: string) {
    loadingInfo.status = true

    if (!isLogin) {
      loadingInfo.status = false
      modalInfo.status = true
      modalInfo.title = t("I'm sorry!")
      modalInfo.content = t('To use the Mimir Watch, please first Sign up or Login!')
      modalInfo.msg = "chatnow"
      return
    }

    loadingInfo.title = t('Saving Sentence')
    const saveStEl = document.getElementById("st" + sentence.id) as HTMLDivElement

    const comment = {
      transcript_id: sentence.id,
      video_id: videoId,
      en_text: sentence.en_text,
      zh_text: sentence.text,
      // foreign_text: sentence.zh_text,
      language: videoLang.value
    }

    axiosVideoService.saveSentence(localStorage.token, comment)
      .then((res: any) => {
        loadingInfo.status = false
        saveStEl?.classList.add('dark')
        console.log('✅ Sentence saved:', res.data)
      })
      .catch((err: any) => {
        modalInfo.status = true
        modalInfo.title = t('somethingWrong')
        modalInfo.content = t('Sorry, failed to save the sentence. Please try again.')
        modalInfo.msg = "confirm"
        loadingInfo.status = false
        console.error('❌ Failed to save sentence:', err)
      })
  }

  /**
   * 查詢單字翻譯資料
   */

function getDictionaryLanguage(learningLanguage: string) {
  switch (learningLanguage) {
    case 'English':
      return 'Spanish'

    case 'Spanish':
    case 'Simplified_Chinese':
      return 'English'

    default:
      return 'English'
  }
}
function lookup(transcriptId: string, vocabulary: string, videoId: string) {
  const userStore = useUserStore();

  loadingInfo.status = true
  loadingInfo.title = t('Looking up vocabulary')

  const lookUpVocbs = {
    video_id: videoId,
    text: vocabulary,
    transcript_id: transcriptId,
    lang_from: learning_language.value,
    lang_to: getDictionaryLanguage(learning_language.value),
  }

  console.log('🔍 Lookup Payload:', lookUpVocbs)

  axiosVideoService.VocabaryLookUp(localStorage.token, lookUpVocbs)
    .then((res: any) => {
      loadingInfo.status = false

      if (res?.data?.id === undefined) {
        vocabularyData.value = {
          id: '',
          text: vocabulary,
          user_saved: false,
          pronunciation: '',
          meaning: 'No translation found',
        }
      } else {
        vocabularyData.value = {
          ...res.data,
          user_saved: !!res.data.user_saved,
        }
      }
    })
    .catch((err: any) => {
      loadingInfo.status = false

      const status = err?.response?.status

      if (status === 401) {
        modalInfo.status = true
        modalInfo.title = t("I'm sorry!")
        modalInfo.content = t('To use the Mimir Watch, please first Sign up or Login!')
        modalInfo.msg = "chatnow"
        return
      }

      if (status === 404) {
        vocabularyData.value = { id: '', text: '', user_saved: false }
        modalInfo.status = true
        modalInfo.type = 'alert'
        modalInfo.title = t('vocabularyNotFoundTitle')
        modalInfo.content = t('vocabularyNotFound', { word: vocabulary })
        modalInfo.msg = 'confirm'
        return
      }

      console.error('❌ Vocabulary lookup failed:', err)
    })
}

  /**
   * 儲存或取消儲存單字至 Notebook
   */
  function saveVb(vocabulary: any) {
    loadingInfo.status = true

    if (!isLogin) {
      loadingInfo.status = false
      modalInfo.status = true
      modalInfo.title = t("I'm sorry!")
      modalInfo.content = t('To use the Mimir Watch, please first Sign up or Login!')
      modalInfo.msg = "chatnow"
      return
    }

    // 記住原本狀態（用來 rollback）
    const prevSaved = vocabularyData.value.user_saved

    // 根據目前狀態顯示 loading 標題
    loadingInfo.title = prevSaved
      ? t('Unsaving Vocabulary')
      : t('Saving Vocabulary')

    axiosWithToken
      .post("/api/v1/vocabulary/save/" + vocabulary.id)
      .then(() => {
        // ✅ 成功才切換狀態
        vocabularyData.value.user_saved = !prevSaved
        loadingInfo.status = false
        console.log('✅ Vocabulary save/un-save success')
      })
      .catch((err: any) => {
        // ❌ 失敗 → 還原狀態（其實這裡不用動，因為根本沒改）
        vocabularyData.value.user_saved = prevSaved

        modalInfo.status = true
        modalInfo.title = t('somethingWrong')
        modalInfo.content = t('freeVocabularyLimit')
        modalInfo.msg = "confirm"

        loadingInfo.status = false
        console.error('❌ Save vocabulary failed:', err)
      })

  }



  return {
    loadingInfo,
    modalInfo,
    vocabularyData,
    saveSt,
    lookup,
    saveVb
  }
}
